import { useState } from 'react';
import type { GridApi } from 'ag-grid-community';
import ConversationGrid from './ConversationGrid';
import ConversationList from './ConversationList';
import ConversationTags from './ConversationTags';
import {
  useMessageFocusIndex,
  useConversationFocusIndex,
} from '../../hooks/useConversationMessages';
import type { Resource, ChatConversation, ChatMessage } from '../../types/resource';

interface Props {
  projectId: string;
  collectionId: string;
  resourceId: string;
  resource: Resource;
  // MANAGE_TAGS — gates the header conversation-tag UI.
  canTag?: boolean;
  // Lifted to the footer Save dropdown (index.tsx).
  onMessagesSelectionChange?: (rows: ChatMessage[]) => void;
  onMessagesGridApi?: (api: GridApi | null, teardown?: boolean) => void;
}

/**
 * Full-width conversation view. Mirrors Angular's layout (resource.html): when a chat resource
 * enters "View in Conversation" (or the resource is phone-image metadata), the entire split-pane
 * viewer is replaced by this — a header (title + conversation tags) over a two-column body:
 * col-4 conversation list, col-8 messages. Selecting a conversation swaps the messages grid to
 * that conversation's anchor message. Conversation tags (sub-phase C) slot into the header.
 */
export default function ConversationView({
  projectId,
  collectionId,
  resourceId,
  resource,
  canTag,
  onMessagesSelectionChange,
  onMessagesGridApi,
}: Props) {
  const basename = resource.filename ? resource.filename.replace(/\.[^.]+$/, '') : '';

  // Active conversation drives the messages column. Defaults to the resource's own anchor
  // (the message the user opened / "View in Conversation" was clicked on).
  const [active, setActive] = useState<{ messageId: string; conversationId: string | null }>({
    messageId: resource.chatMessageId,
    conversationId: resource.chatConversationId,
  });

  // Focus indices apply only to the initial anchor conversation (GetMessageIndex /
  // GetConversationIndex are keyed to resource.chatMessageId). Once the user picks another
  // conversation there is no focus target.
  const isAnchor = active.messageId === resource.chatMessageId;
  const { data: messageFocusIndex } = useMessageFocusIndex(
    projectId, collectionId, resourceId, resource.chatConversationId, resource.chatMessageId, isAnchor,
  );
  const { data: conversationFocusIndex } = useConversationFocusIndex(
    projectId, collectionId, resourceId, resource.chatMessageId, true,
  );

  const handleSelect = (c: ChatConversation) => {
    setActive({ messageId: c.mostRecentMessage.messageId, conversationId: c.conversationId });
    // Switching conversations clears the message selection (the grid remounts).
    onMessagesSelectionChange?.([]);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, width: '100%' }}>
      <div
        className="page-header resource-header-content"
        style={{ marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}
      >
        <h3 style={{ margin: 0, flex: '0 1 auto', minWidth: 0 }} data-testid="viewer-conversation-title-heading">
          <i className="fas fa-comments" />{' '}
          {resource.title || resource.custodianName || resource.filename || '—'}
          {(resource.collectionName || basename) && (
            <small style={{ marginLeft: 8 }}>
              {resource.collectionName} <span className="text-muted">/</span> {basename}
            </small>
          )}
        </h3>
        {canTag && active.messageId && (
          // Right margin clears the absolutely-positioned detach button (top:7 right:7),
          // mirroring Angular's float:right; margin-right:25px on the tag block.
          <div style={{ flex: '0 1 auto', minWidth: 0, marginRight: 25 }}>
            <ConversationTags
              projectId={projectId}
              collectionId={collectionId}
              resourceId={resourceId}
              chatMessageId={active.messageId}
              announceUntag={resource.type === 'phone image metadata'}
            />
          </div>
        )}
      </div>

      <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: 24 }}>
        <div style={{ flex: '0 0 33%', minWidth: 0, minHeight: 0, paddingLeft: 15, boxSizing: 'border-box' }}>
          <ConversationList
            projectId={projectId}
            collectionId={collectionId}
            resourceId={resourceId}
            activeConversationId={active.conversationId}
            focusIndex={conversationFocusIndex}
            onSelect={handleSelect}
          />
        </div>
        <div style={{ flex: '1 1 auto', minWidth: 0, minHeight: 0 }}>
          {active.messageId && (
            <ConversationGrid
              key={active.messageId}
              projectId={projectId}
              collectionId={collectionId}
              resourceId={resourceId}
              messageId={active.messageId}
              focusIndex={isAnchor ? messageFocusIndex : null}
              onSelectionChange={onMessagesSelectionChange}
              onGridApi={onMessagesGridApi}
            />
          )}
        </div>
      </div>
    </div>
  );
}
